import styled from 'styled-components';
import {query, space} from '@theme/fn';
import Link from '@components/Link';
import {GetStaticProps} from 'next';

import {font} from '@design/templateFns';
import {getBlogPostPaths, getBlogPostData} from '@data/blogListingPage';
import getSiteConfig from '@data/siteConfig';
import Layout from '@components/Layout/Layout';
import Stretch from '@components/Layout/Stretch';
import Page from '@components/Page';
import AnnouncementBar from '@components/AnnouncementBarComponent/mapSiteConfig';
import Navigation from '@components/Navigation/mapSiteConfig';
import {Footer} from '@components/FooterComponent';
import {PageDocument, SiteConfig} from '@data/types';
import {Block} from '@components/Layout';

import {Breadcrumbs} from '@components/Breadcrumbs';
import {NewsDetailHero, NewsDetailHeroProps} from '@components/NewsDetailHero';
import {InteriorHero, InteriorHeroProps} from '@components/InteriorHero';
import {CircleArrow} from '@components/Arrow/index';

import {BlogPost} from '@schema/types';
import {ResolvedSanityReferences} from '@data/types';

export async function getStaticPaths() {
  const paths = (await getBlogPostPaths()).map((path) => ({
    params: {
      slug: path,
    },
  }));
  return {
    paths,
    fallback: 'blocking',
  };
}

// misc
import log from '@util/logging';
import ProseBlock from '@components/ProseBlock';

export interface slugPageContext {
  params: {
    // because this is an _optional_ catch-all page, next will usually set this
    // to an array of path segments. But for the root path, it isn't set at all.
    slug?: string;
  };
  preview?: boolean;
}

export interface slugPageProps {
  page: BlogData;
  siteConfig: SiteConfig;
  preview?: boolean;
}

export const getStaticProps: GetStaticProps = async (
  context: slugPageContext,
) => {
  const {params, preview} = context;
  // if params.slug is missing this is the root, re-map to home
  const slug = params.slug;
  try {
    const pageData = await getBlogPostData(slug, preview);

    if (!pageData) {
      return {notFound: true};
    }

    const siteConfig = await getSiteConfig(); //TODO: @platform site config as a provider
    return {
      props: {
        page: pageData,
        siteConfig: siteConfig,
        preview: !!preview,
      } as unknown as slugPageProps,
    };
  } catch (e) {
    log.error(e);
    throw e; // best way to get 500 page?
  }
};

// TYPES
type BlogData = ResolvedSanityReferences<BlogPost>;

export const MappedInteriorHero = (block: BlogData) => {
  const props: InteriorHeroProps = {
    imgs: {
      desktop: block?.mainImage,
      mobile: block?.mainImage,
    },
    narrow: true,
    koppersFullBleed: undefined,
  };

  return <InteriorHero {...props} />;
};

// Back Button
const MoreCTALink = styled.a`
  cursor: pointer;
  text-decoration: none;
  display: flex;
  color: rgba(0, 0, 0, 1);
  font-weight: 700;
  font-size: 14px;
  align-items: center;

  @media screen and (${query.atLeast('desktop')}) {
    font-size: 18px;
  }
`;

const CTAText = styled.div`
  ${font('body-lg-regular')};
  font-size: 20px;
  font-weight: 700;
  margin-left: ${space('sm')};
`;

// Slugs to use
const slugPrefix = '/company/community/blog';

const BlogPostPage = (props: slugPageProps) => {
  const {page} = props;

  /* TODO: @platform: Make slug paths a cms object,
   * eliminate these [slug] pages as  the main page should be able to render
   * them once slugs aren't an issue anymore.
   */

  // Our Company / In the Community / Koppers Connection / [article title]
  const breadcrumbData = [
    {
      title: 'Our Company',
      slug: {current: '/company'},
    },
    {
      title: 'In the Community',
      slug: {current: '/company/community'},
    },
    {
      title: 'Koppers Connection',
      slug: {current: '/company/community/blog'},
    },
    {
      title: page.title,
      slug: {current: page.slug?.current},
    },
  ];

  // SOCIAL MEDIA SHARE URLs
  const site = 'https://www.austenriggs.org/';
  const urlFacebook = `https://www.facebook.com/sharer.php?u=${encodeURIComponent(
    `${site}${page.slug?.current}`,
  )}&[title]=${encodeURIComponent(page.title)}`;
  const urlTwitter = `https://twitter.com/intent/tweet?url=${encodeURIComponent(
    `${site}${page.slug?.current}`,
  )}&text=${encodeURIComponent(page.title)}`;
  const urlLinkedin = `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(
    `${site}${page.slug?.current}`,
  )}`;

  // MAPPED COMPONENTS
  const MappedNewsDetailHero = (block: BlogData) => (
    <NewsDetailHero
      {...{
        category: block.postType?.title || 'News',
        header: block.title,
        date: block.publishedAt,
        urlFacebook,
        urlTwitter,
        urlLinkedin,
        squish: true,
      }}
    />
  );

  return (
    <Page
      {...{
        description: page.shortDescription,
        metatitle: page.title,
        openGraphImage: page.mainImage,
      }}
    >
      <Layout>
        <AnnouncementBar {...(props.siteConfig as SiteConfig)} />
        <Navigation {...(props.siteConfig as SiteConfig)} />
        <Breadcrumbs pages={breadcrumbData} />
        <MappedNewsDetailHero {...page} />
        <MappedInteriorHero {...page} />
        <ProseBlock block={{...page.body, _key: page._id}} />
        <Block>
          <Link href={slugPrefix}>
            <MoreCTALink>
              <CircleArrow circleColor="#01617A" flip />{' '}
              <CTAText>Back to Koppers Connection</CTAText>
            </MoreCTALink>
          </Link>
        </Block>
        <Stretch />
        <Footer siteConfig={props.siteConfig as SiteConfig} />
      </Layout>
    </Page>
  );
};

export default BlogPostPage;
